// Pure helpers for the CVC migration page: VispApi URL builders, error text,
// verify-result merging and the report CSVs live here so the Vue components
// stay thin and every branch is unit-testable.

import { toCsv } from '@/utils/csv';
import { SYDNEY, POI_TIMEZONE, ZONE_LABEL, formatInZone } from '@/utils/zonedTime';

const BASE = '/api/Visp/CvcMigration';
const withRefresh = (url, refresh) => (refresh ? `${url}?refresh=true` : url);

// One builder per VispApi endpoint. `refresh` marks a poll: the server skips
// the audit row for it. A target host id of 0 asks the server to resolve it.
export function buildListCvcsUrl () {
  return `${BASE}/ListCvcs`;
}

export function buildListHostsUrl () {
  return `${BASE}/ListHosts`;
}

export function buildEnvironmentUrl () {
  return `${BASE}/Environment`;
}

export function buildPreflightUrl (cvcId, targetHostId, { refresh } = {}) {
  return withRefresh(`${BASE}/PreFlight/${cvcId}/${targetHostId}`, refresh);
}

export function buildSetWindowUrl (cvcId, targetHostId) {
  return `${BASE}/SetCutoverWindow/${cvcId}/${targetHostId}`;
}

export function buildPreAssignStaticsUrl (cvcId, targetHostId) {
  return `${BASE}/PreAssignStatics/${cvcId}/${targetHostId}`;
}

export function buildReleasePreAssignmentsUrl (cvcId) {
  return `${BASE}/ReleasePreAssignments/${cvcId}`;
}

export function buildIpChangeReportUrl (cvcId, targetHostId) {
  return `${BASE}/IpChangeReport/${cvcId}/${targetHostId}`;
}

export function buildBuildOnTargetUrl (cvcId, targetHostId) {
  return `${BASE}/BuildOnTarget/${cvcId}/${targetHostId}`;
}

export function buildRepolicyUrl (cvcId) {
  return `${BASE}/RepolicyOnTarget/${cvcId}`;
}

export function buildVerifyTargetUrl (cvcId, targetHostId) {
  return `${BASE}/VerifyTarget/${cvcId}/${targetHostId}`;
}

export function buildRemoveTargetCopiesUrl (cvcId) {
  return `${BASE}/RemoveTargetCopies/${cvcId}`;
}

export function buildShutdownSourceUrl (cvcId) {
  return `${BASE}/ShutdownSource/${cvcId}`;
}

export function buildRollbackSourceUrl (cvcId) {
  return `${BASE}/RollbackSource/${cvcId}`;
}

export function buildRestoreOldIpsUrl (cvcId) {
  return `${BASE}/RestoreOldIps/${cvcId}`;
}

export function buildConfirmRepointUrl (cvcId) {
  return `${BASE}/ConfirmRepoint/${cvcId}`;
}

export function buildActivateUrl (cvcId) {
  return `${BASE}/Activate/${cvcId}`;
}

export function buildCommitStaticsUrl (cvcId) {
  return `${BASE}/CommitStatics/${cvcId}`;
}

export function buildKickAllUrl (cvcId) {
  return `${BASE}/KickAll/${cvcId}`;
}

export function buildVerifyReconnectedUrl (cvcId, { refresh } = {}) {
  return withRefresh(`${BASE}/VerifyReconnected/${cvcId}`, refresh);
}

export function buildMarkMigratedUrl (cvcId) {
  return `${BASE}/MarkMigrated/${cvcId}`;
}

export function buildRemoveSourceCopiesUrl (cvcId) {
  return `${BASE}/RemoveSourceCopies/${cvcId}`;
}

export function buildCancelMigrationUrl (cvcId) {
  return `${BASE}/CancelMigration/${cvcId}`;
}

// The rollback after the re-point confirm.
export function buildStartRollbackUrl (cvcId) {
  return `${BASE}/StartRollback/${cvcId}`;
}

export function buildRollbackWindowUrl (cvcId) {
  return `${BASE}/RollbackWindow/${cvcId}`;
}

export function buildStopRollbackUrl (cvcId) {
  return `${BASE}/StopRollback/${cvcId}`;
}

export function buildConfirmRepointBackUrl (cvcId) {
  return `${BASE}/ConfirmRepointBack/${cvcId}`;
}

export function buildMoveBackUrl (cvcId) {
  return `${BASE}/MoveBack/${cvcId}`;
}

export function buildCloseRolledBackUrl (cvcId) {
  return `${BASE}/CloseRolledBack/${cvcId}`;
}

export function buildBuildOnSourceUrl (cvcId) {
  return `${BASE}/BuildOnSource/${cvcId}`;
}

export function buildRepolicyOnSourceUrl (cvcId) {
  return `${BASE}/RepolicyOnSource/${cvcId}`;
}

export function buildRemoveFromSourceUrl (cvcId) {
  return `${BASE}/RemoveFromSource/${cvcId}`;
}

const DEFAULT_ERROR_TEXT = 'CVC migration request failed';
const SESSION_EXPIRED_TEXT = 'Session expired - reload the page (progress is kept on the server)';
const DID_NOT_ANSWER_TEXT = 'VispApi or Usher did not answer in time. Refresh to see whether the step ran.';
const ACCESS_DENIED_TEXT = 'Access denied';

// VispApi answers a failure with a string or a `{ message, data }` object, so
// `response.data` can not go straight into a toast.
export function errorMessage (e) {
  if (!e) return DEFAULT_ERROR_TEXT;
  const response = e.response;
  const status = response && response.status;
  if (status === 401) return SESSION_EXPIRED_TEXT;
  // Gateway errors carry an HTML body; a request that left but got no reply
  // (timeout, network error) has a request and no response.
  if (status === 502 || status === 504 || (!response && (e.code === 'ECONNABORTED' || e.request))) {
    return DID_NOT_ANSWER_TEXT;
  }
  const body = response && response.data;
  if (body && typeof body === 'object' && typeof body.message === 'string' && body.message) return body.message;
  if (typeof body === 'string' && body) return body;
  return (typeof e.message === 'string' && e.message) || DEFAULT_ERROR_TEXT;
}

// 409 is a refusal the operator can act on (amber); everything else is an error.
export function errorSeverity (e) {
  return e && e.response && e.response.status === 409 ? 'warning' : 'error';
}

// A non-admin gets this text from the repository, or an empty 403 from the
// admin filter on the QA API.
export function isAccessDenied (e) {
  return !!(e && e.response && e.response.status === 403) || errorMessage(e) === ACCESS_DENIED_TEXT;
}

export function chunk (ids, size) {
  const list = ids || [];
  const step = Math.max(1, Math.floor(size) || 1);
  const chunks = [];
  for (let i = 0; i < list.length; i += step) chunks.push(list.slice(i, i + step));
  return chunks;
}

// Display only: joins the answers of the sequential verify calls. The server
// stamps readiness per call, so the last call holds the current targetReady.
export function mergeVerifyResults (results) {
  const list = (results || []).filter(Boolean);
  const last = list[list.length - 1] || {};
  const total = (key) => list.reduce((sum, r) => sum + (r[key] || 0), 0);
  const seen = {};
  const unexpected = [];
  list.forEach(r => (r.unexpected || []).forEach(u => {
    const key = u.copyId !== undefined ? u.copyId : JSON.stringify(u);
    if (!seen[key]) {
      seen[key] = true;
      unexpected.push(u);
    }
  }));
  return {
    cvcId: last.cvcId,
    avcsChecked: total('avcsChecked'),
    avcsPresent: total('avcsPresent'),
    avcsMissing: total('avcsMissing'),
    avcsMismatched: total('avcsMismatched'),
    ready: list.length > 0 && list.every(r => r.ready === true),
    targetReady: last.targetReady === undefined ? null : last.targetReady,
    cvcInterface: last.cvcInterface || null,
    loopInterface: last.loopInterface || null,
    details: list.reduce((all, r) => all.concat(r.details || []), []),
    unexpected,
  };
}

export function hasPendingEvents (preflight) {
  return !!(preflight && preflight.steps && preflight.steps.pendingEvents);
}

// The IP change report for the wholesalers. One line per
// service; the CVC and the window repeat on each line so a file split per
// wholesaler still says what it is about. `wholesaler` is a wholesalerName;
// leave it out for every wholesaler.
export function toIpReportCsv (report, { wholesaler } = {}) {
  const zone = POI_TIMEZONE[report.poiState] || SYDNEY;
  const sydney = (utc) => formatInZone(utc, SYDNEY, { year: true });
  const local = (utc) => {
    const text = formatInZone(utc, zone, { year: true });
    return text && `${text} ${ZONE_LABEL[zone]}`;
  };
  const columns = [
    { header: 'CVC', value: () => report.cvcName },
    { header: 'Window start (Sydney)', value: () => sydney(report.windowStartUtc) },
    { header: 'Window end (Sydney)', value: () => sydney(report.windowEndUtc) },
    { header: 'Window start (local)', value: () => local(report.windowStartUtc) },
    { header: 'Window end (local)', value: () => local(report.windowEndUtc) },
    { header: 'Wholesaler', value: 'wholesalerName' },
    { header: 'Account', value: 'accountNumber' },
    { header: 'AVC', value: 'avcId' },
    { header: 'Service id', value: 'radiusServiceId' },
    { header: 'Username', value: 'radiusUsername' },
    { header: 'Service name', value: 'serviceName' },
    { header: 'Service address', value: 'serviceAddress' },
    { header: 'Protocol', value: 'protocol' },
    { header: 'IP type', value: 'ipType' },
    { header: 'Current IP', value: 'currentIp' },
    { header: 'New IP', value: 'newIp' },
    { header: 'Change', value: 'change' },
  ];
  const rows = (report.rows || []).filter(r => !wholesaler || r.wholesalerName === wholesaler);
  return { filename: ipReportFilename(report), text: toCsv(columns, rows) };
}

// "{cvc}_IP_changes_{yyyy-mm-dd}_{HHmm}_SYD.csv" from the window start in Sydney time.
function ipReportFilename (report) {
  const name = String(report.cvcName).replace(/[\\/:*?"<>|]/g, '-');
  const start = formatInZone(report.windowStartUtc, SYDNEY, { year: true });
  if (!start) return `${name}_IP_changes_no-window.csv`;
  const [day, time] = start.split(' ');
  const [dd, mm, yyyy] = day.split('/');
  return `${name}_IP_changes_${yyyy}-${mm}-${dd}_${time.replace(':', '')}_SYD.csv`;
}
